import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  BarChart,
  Bar,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface UsersDashboardProps {
  apiBaseUrl: string;
}

interface SeriesPoint {
  date: string;
  formattedDate: string;
  newUsers: number;
  cumulativeUsers: number;
  cumulativeDevices: number;
}

interface UserMetrics {
  totalUsers: number;
  totalDevices: number;
  devicesPerUser: number;
  byRole: { role: string; count: number }[];
  activity: { active24h: number; active7d: number; active30d: number; withLogin: number; never: number };
  betaTesters: number;
  usersWithApp: number;
  series: {
    dailyData: SeriesPoint[];
    monthlyData: SeriesPoint[];
    yearlyData: SeriesPoint[];
  };
}

type TimeUnit = 'day' | 'month' | 'year';
interface TimeRange {
  unit: TimeUnit;
  value: number;
  label: string;
}

const TIME_RANGES: TimeRange[] = [
  { unit: 'day', value: 30, label: '1 Month' },
  { unit: 'day', value: 90, label: '3 Months' },
  { unit: 'month', value: 6, label: '6 Months' },
  { unit: 'month', value: 12, label: '1 Year' },
  { unit: 'year', value: 0, label: 'All Time' },
];

const COLOR_USERS = '#4e73df';
const COLOR_DEVICES = '#1cc88a';
const ROLE_COLORS: Record<string, string> = {
  customer: '#1cc88a',
  admin: '#4e73df',
  developer: '#6f42c1',
  technician: '#f6c23e',
  support: '#36b9cc',
  factory: '#fd7e14',
  unknown: '#858796',
};

function sliceSeries(series: UserMetrics['series'], range: TimeRange): SeriesPoint[] {
  let data =
    range.unit === 'day' ? series.dailyData : range.unit === 'month' ? series.monthlyData : series.yearlyData;
  data = [...(data || [])].sort((a, b) => a.date.localeCompare(b.date));
  if (range.value > 0 && data.length > range.value) return data.slice(data.length - range.value);
  return data;
}

const RangeButtons: React.FC<{ value: TimeRange; onChange: (r: TimeRange) => void }> = ({ value, onChange }) => (
  <div className="d-flex flex-wrap gap-1">
    {TIME_RANGES.map((range) => (
      <button
        key={range.label}
        className={`btn btn-sm ${value.label === range.label ? 'btn-primary' : 'btn-outline-primary'}`}
        onClick={() => onChange(range)}
      >
        {range.label}
      </button>
    ))}
  </div>
);

const SummaryCard: React.FC<{ label: string; value: number | string; color?: string }> = ({ label, value, color }) => (
  <div className="col-6 col-md">
    <div className="card bg-light h-100">
      <div className="card-body py-2">
        <div className="small text-muted d-flex align-items-center">
          {color && (
            <span
              style={{
                display: 'inline-block',
                width: '0.75rem',
                height: '0.75rem',
                borderRadius: '0.2rem',
                backgroundColor: color,
                marginRight: '0.4rem',
              }}
            />
          )}
          {label}
        </div>
        <div className="h5 mb-0">{typeof value === 'number' ? value.toLocaleString() : value}</div>
      </div>
    </div>
  </div>
);

const NewSignupsChart: React.FC<{ series: UserMetrics['series'] }> = ({ series }) => {
  const [range, setRange] = useState<TimeRange>(TIME_RANGES[3]);
  const data = sliceSeries(series, range);
  const total = data.reduce((s, p) => s + (p.newUsers || 0), 0);
  const isDay = range.unit === 'day';

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title mb-0">New Sign-ups</h5>
        <div className="text-muted small">New user accounts created per period</div>
      </div>
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <RangeButtons value={range} onChange={setRange} />
          <span className="badge bg-light text-dark">{total.toLocaleString()} in period</span>
        </div>
        {total === 0 ? (
          <div className="alert alert-info text-center py-4 mb-0">No sign-ups in the selected period.</div>
        ) : (
          <div style={{ height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 5, right: 30, left: 10, bottom: isDay ? 60 : 30 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="formattedDate"
                  angle={isDay ? -45 : 0}
                  textAnchor={isDay ? 'end' : 'middle'}
                  height={isDay ? 60 : 30}
                  tickMargin={isDay ? 15 : 8}
                  interval={isDay && data.length > 31 ? Math.floor(data.length / 15) : 0}
                />
                <YAxis allowDecimals={false} />
                <Tooltip formatter={(value: number) => [`${value} sign-ups`, 'New users']} />
                <Bar dataKey="newUsers" name="New users" fill={COLOR_USERS} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

const GrowthChart: React.FC<{ series: UserMetrics['series'] }> = ({ series }) => {
  const [range, setRange] = useState<TimeRange>(TIME_RANGES[4]);
  const data = sliceSeries(series, range);
  const isDay = range.unit === 'day';

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title mb-0">Accounts vs. Devices</h5>
        <div className="text-muted small">Cumulative user accounts compared to the device fleet over time</div>
      </div>
      <div className="card-body">
        <div className="mb-3">
          <RangeButtons value={range} onChange={setRange} />
        </div>
        <div style={{ height: '320px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: isDay ? 60 : 30 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="formattedDate"
                angle={isDay ? -45 : 0}
                textAnchor={isDay ? 'end' : 'middle'}
                height={isDay ? 60 : 30}
                tickMargin={isDay ? 15 : 8}
                interval={isDay && data.length > 31 ? Math.floor(data.length / 15) : 0}
              />
              <YAxis yAxisId="users" allowDecimals={false} stroke={COLOR_USERS} />
              <YAxis yAxisId="devices" orientation="right" allowDecimals={false} stroke={COLOR_DEVICES} />
              <Tooltip />
              <Legend />
              <Line
                yAxisId="users"
                type="monotone"
                dataKey="cumulativeUsers"
                name="User accounts"
                stroke={COLOR_USERS}
                strokeWidth={2}
                dot={false}
              />
              <Line
                yAxisId="devices"
                type="monotone"
                dataKey="cumulativeDevices"
                name="Devices"
                stroke={COLOR_DEVICES}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const RolesChart: React.FC<{ byRole: UserMetrics['byRole'] }> = ({ byRole }) => {
  const data = byRole.map((r) => ({ ...r, label: r.role.charAt(0).toUpperCase() + r.role.slice(1) }));
  return (
    <div className="card h-100">
      <div className="card-header">
        <h5 className="card-title mb-0">Accounts by Role</h5>
        <div className="text-muted small">Breakdown of account types</div>
      </div>
      <div className="card-body">
        <div style={{ height: `${Math.max(220, data.length * 46)}px` }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="label" width={90} />
              <Tooltip formatter={(value: number) => [`${value} accounts`, 'Count']} />
              <Bar dataKey="count" name="Accounts" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                {data.map((entry) => (
                  <Cell key={entry.role} fill={ROLE_COLORS[entry.role] || ROLE_COLORS.unknown} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const ActivityChart: React.FC<{ activity: UserMetrics['activity']; totalUsers: number }> = ({ activity }) => {
  const data = useMemo(() => {
    const within1d = activity.active24h;
    const within7d = Math.max(activity.active7d - activity.active24h, 0);
    const within30d = Math.max(activity.active30d - activity.active7d, 0);
    const older = Math.max(activity.withLogin - activity.active30d, 0);
    return [
      { label: 'Today', count: within1d },
      { label: '2–7 days', count: within7d },
      { label: '8–30 days', count: within30d },
      { label: '30+ days', count: older },
      { label: 'Never', count: activity.never },
    ];
  }, [activity]);

  return (
    <div className="card h-100">
      <div className="card-header">
        <h5 className="card-title mb-0">Account Activity</h5>
        <div className="text-muted small">Accounts by last app session (login / session refresh)</div>
      </div>
      <div className="card-body">
        <div style={{ height: '250px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" />
              <YAxis allowDecimals={false} />
              <Tooltip formatter={(value: number) => [`${value} accounts`, 'Count']} />
              <Bar dataKey="count" name="Accounts" fill={COLOR_USERS} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const UsersDashboard: React.FC<UsersDashboardProps> = ({ apiBaseUrl }) => {
  const [metrics, setMetrics] = useState<UserMetrics | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${apiBaseUrl}/users/metrics`, { withCredentials: true });
        if (cancelled) return;
        setMetrics(res.data);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching user metrics:', err);
        setError('Failed to fetch user metrics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl]);

  if (loading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error && !metrics) {
    return <div className="alert alert-danger">{error}</div>;
  }

  if (!metrics) {
    return <div className="alert alert-warning">No user data available</div>;
  }

  const customers = metrics.byRole.find((r) => r.role === 'customer')?.count ?? 0;

  return (
    <div>
      {/* Summary */}
      <div className="row g-2 mb-4">
        <SummaryCard label="Total accounts" value={metrics.totalUsers} />
        <SummaryCard label="Customers" value={customers} color={ROLE_COLORS.customer} />
        <SummaryCard label="Active (30d)" value={metrics.activity.active30d} color={COLOR_USERS} />
        <SummaryCard label="Beta testers" value={metrics.betaTesters} color={ROLE_COLORS.developer} />
        <SummaryCard label="App installs" value={metrics.usersWithApp} color={ROLE_COLORS.technician} />
        <SummaryCard label="Devices / account" value={metrics.devicesPerUser.toLocaleString()} color={COLOR_DEVICES} />
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <NewSignupsChart series={metrics.series} />
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <GrowthChart series={metrics.series} />
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12 col-lg-6 mb-4 mb-lg-0">
          <RolesChart byRole={metrics.byRole} />
        </div>
        <div className="col-12 col-lg-6">
          <ActivityChart activity={metrics.activity} totalUsers={metrics.totalUsers} />
        </div>
      </div>
    </div>
  );
};

export default UsersDashboard;
